import { logAdminAction } from "./auditLog";
import { supabase } from "./supabase";

export type ManagerRow = {
  user_id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  assigned_count: number;
  /** Last sign-in / activity. Null if the user has never signed in. */
  last_active: string | null;
};

export type ManagerAssignmentRow = {
  manager_id: string;
  competition_id: string;
  company_id: string;
  assigned_by: string | null;
  assigned_at: string;
};

/**
 * List the operational delegates ("limited clients") for a company.
 *
 * Since the Manager portal collapsed into a reduced-permission Client, a
 * delegate is no longer a company_users member with role='manager' — they're
 * a role='client' user with NO company membership, scoped purely by
 * `company_manager_assignments`. So we enumerate distinct `manager_id`s from
 * the assignment table (the source of truth for the limited-client surface)
 * and hydrate profiles. A delegate appears once they hold ≥1 assignment.
 */
export async function listManagersForCompany(companyId: string): Promise<ManagerRow[]> {
  const { data: assignments } = await supabase
    .from("company_manager_assignments")
    .select("manager_id")
    .eq("company_id", companyId);

  const countByManager = new Map<string, number>();
  for (const a of (assignments ?? []) as { manager_id: string }[]) {
    countByManager.set(a.manager_id, (countByManager.get(a.manager_id) ?? 0) + 1);
  }
  const ids = [...countByManager.keys()];
  if (ids.length === 0) return [];

  const { data: profiles } = await supabase
    .from("user_profiles")
    .select("id, email, full_name, avatar_url")
    .in("id", ids);
  const profileMap = new Map(
    ((profiles ?? []) as { id: string; email: string; full_name: string | null; avatar_url: string | null }[]).map(
      (p) => [p.id, p]
    )
  );

  const out: ManagerRow[] = ids.map((id) => {
    const p = profileMap.get(id);
    return {
      user_id: id,
      email: p?.email ?? "",
      full_name: p?.full_name ?? null,
      avatar_url: p?.avatar_url ?? null,
      assigned_count: countByManager.get(id) ?? 0,
      // last_active intentionally null — we don't snapshot last-seen on
      // user_profiles in this schema. Surface as "—" in the UI.
      last_active: null,
    };
  });
  out.sort((a, b) => (a.full_name ?? a.email).localeCompare(b.full_name ?? b.email));
  return out;
}

/** Per-manager assignment list — competition_ids only. */
export async function listAssignmentsForManager(managerId: string): Promise<string[]> {
  const { data } = await supabase
    .from("company_manager_assignments")
    .select("competition_id")
    .eq("manager_id", managerId);
  return ((data ?? []) as { competition_id: string }[]).map((r) => r.competition_id);
}

/** Inverse view: every manager assigned to a given competition. */
export async function listManagersForCompetition(
  competitionId: string
): Promise<{ manager_id: string; assigned_at: string }[]> {
  const { data } = await supabase
    .from("company_manager_assignments")
    .select("manager_id, assigned_at")
    .eq("competition_id", competitionId);
  return (data ?? []) as { manager_id: string; assigned_at: string }[];
}

/**
 * Replace a manager's assignment set in a single transaction.
 * Computes the diff client-side, then runs INSERT/DELETE in
 * sequence. The CMA table's PK (manager, comp) means re-INSERT of
 * existing rows would fail, hence the diff. Returns the new count
 * for optimistic UI.
 */
export async function setManagerAssignments(
  managerId: string,
  companyId: string,
  competitionIds: string[],
  assignedBy: string | null
): Promise<{ count: number; error: Error | null }> {
  const current = await listAssignmentsForManager(managerId);
  const target = new Set(competitionIds);
  const currentSet = new Set(current);

  const toAdd = competitionIds.filter((c) => !currentSet.has(c));
  const toRemove = current.filter((c) => !target.has(c));

  if (toAdd.length > 0) {
    const { error } = await supabase
      .from("company_manager_assignments")
      .insert(
        toAdd.map((cid) => ({
          manager_id: managerId,
          competition_id: cid,
          company_id: companyId,
          assigned_by: assignedBy,
        }))
      );
    if (error) return { count: 0, error: new Error(error.message) };
  }
  if (toRemove.length > 0) {
    const { error } = await supabase
      .from("company_manager_assignments")
      .delete()
      .eq("manager_id", managerId)
      .in("competition_id", toRemove);
    if (error) return { count: 0, error: new Error(error.message) };
  }

  if (toAdd.length > 0 || toRemove.length > 0) {
    await logAdminAction({
      action: "manager.assignments_set",
      entityType: "company_manager_assignments",
      entityId: managerId,
      summary: `Manager assignments updated — ${competitionIds.length} comp(s) total (+${toAdd.length} / -${toRemove.length})`,
      diff: { before: { competition_ids: current }, after: { competition_ids: competitionIds } },
    });
  }

  return { count: competitionIds.length, error: null };
}

/**
 * Invite an operational delegate ("limited client"). They are provisioned as
 * a reduced-permission CLIENT — `role='client'` with NO company_users
 * membership — and scoped to specific competitions via
 * `company_manager_assignments` (set separately by the caller). Because they
 * aren't a company member, every full-client write policy (which gates on
 * `is_company_member`) blocks them server-side; they get the read-mostly
 * `is_manager_of_competition` surface instead.
 *
 * The `limited_client: true` flag tells the `invite-company-user` Edge
 * Function to SKIP the company_users upsert (the bit that would otherwise
 * promote them to a full client). Returns the invite token so the caller can
 * copy/paste a magic link if needed.
 */
export async function inviteManager(input: {
  email: string;
  companyId: string;
  fullName?: string | null;
}): Promise<{ token: string | null; error: Error | null }> {
  const { data: userData } = await supabase.auth.getUser();
  const uid = userData?.user?.id ?? null;
  if (!uid) return { token: null, error: new Error("Not signed in") };

  const { data, error } = await supabase
    .from("invites")
    .insert({
      kind: "manager",
      email: input.email.trim().toLowerCase(),
      role: "client",
      company_id: input.companyId,
      invited_by: uid,
      // 14-day default — same window the client invites use.
      expires_at: new Date(Date.now() + 14 * 86400 * 1000).toISOString(),
    })
    .select("token")
    .single();
  if (error || !data) return { token: null, error: new Error(error?.message ?? "Insert failed") };

  // Fire-and-forget email + auth-user provisioning via the Edge Function.
  // `limited_client: true` makes it provision a reduced-permission client
  // (role='client', no company_users row).
  void supabase.functions.invoke("invite-company-user", {
    body: {
      kind: "manager",
      role: "client",
      limited_client: true,
      email: input.email.trim().toLowerCase(),
      company_id: input.companyId,
      contact_name: input.fullName ?? "",
    },
  });

  await logAdminAction({
    action: "manager.invite",
    entityType: "invites",
    entityId: null,
    summary: `Invited manager ${input.email.trim().toLowerCase()}`,
    diff: { after: { email: input.email.trim().toLowerCase(), company_id: input.companyId } },
  });

  return { token: data.token as string, error: null };
}

/**
 * Remove an operational delegate from the company. A limited client's entire
 * access derives from `company_manager_assignments`, so deleting their
 * assignment rows for this company fully revokes their visibility (RLS
 * recomputes immediately). We do NOT touch `user_roles` (the shared
 * 'client' role) or `company_users` (a limited client has no membership) —
 * the auth account stays intact and simply has nothing scoped to it.
 */
export async function removeManager(
  managerId: string,
  companyId: string
): Promise<{ error: Error | null }> {
  const { error: cmaErr } = await supabase
    .from("company_manager_assignments")
    .delete()
    .eq("manager_id", managerId)
    .eq("company_id", companyId);
  if (cmaErr) return { error: new Error(cmaErr.message) };

  await logAdminAction({
    action: "manager.remove",
    entityType: "company_manager_assignments",
    entityId: managerId,
    summary: `Removed operational delegate from company`,
    diff: { before: { manager_id: managerId, company_id: companyId }, after: null },
  });

  return { error: null };
}

// ─── Manager-side helpers ─────────────────────────────────────────────

export type ManagerAssignedCompetition = {
  id: string;
  name: string;
  status: string;
  company_name: string | null;
  closes_at: string | null;
  assigned_at: string;
};

/** List the competitions assigned to the calling manager. Used by
 * the manager portal sidebar + dashboard. */
export async function listMyAssignedCompetitions(): Promise<ManagerAssignedCompetition[]> {
  const { data: userData } = await supabase.auth.getUser();
  const uid = userData?.user?.id ?? null;
  if (!uid) return [];

  const { data: assignments } = await supabase
    .from("company_manager_assignments")
    .select("competition_id, assigned_at")
    .eq("manager_id", uid);
  const rows = (assignments ?? []) as { competition_id: string; assigned_at: string }[];
  if (rows.length === 0) return [];

  const compIds = rows.map((r) => r.competition_id);
  const { data: comps } = await supabase
    .from("competitions")
    .select("id, name, status, closes_at, companies(name)")
    .in("id", compIds);

  type CompRow = {
    id: string;
    name: string;
    status: string;
    closes_at: string | null;
    companies: { name: string } | { name: string }[] | null;
  };

  const assignedAtById = new Map(rows.map((r) => [r.competition_id, r.assigned_at]));
  return ((comps ?? []) as CompRow[]).map((c) => {
    const co = Array.isArray(c.companies) ? c.companies[0] : c.companies;
    return {
      id: c.id,
      name: c.name,
      status: c.status,
      company_name: co?.name ?? null,
      closes_at: c.closes_at,
      assigned_at: assignedAtById.get(c.id) ?? "",
    };
  });
}
